'use client';
import React, { useState } from 'react';
import { ChevronRight, ChevronDown, AlertTriangle, Zap } from 'lucide-react';
import { getRankDotColor, getRankColor, Rank, Distributor } from '@/data/mockData';

interface NetworkTreeProps {
  distributors: Record<string, Distributor>;
  rootId: string;
  selectedId: string;
  onSelect: (id: string) => void;
  maxDepth: number;
  searchQuery: string;
  activeFilter: 'all' | 'active' | 'inactive';
  selectedRank: string;
}

function TreeNode({
  dist,
  distributors,
  depth,
  selectedId,
  onSelect,
  maxDepth,
  searchQuery,
  activeFilter,
  selectedRank,
}: {
  dist: Distributor;
  distributors: Record<string, Distributor>;
  depth: number;
  selectedId: string;
  onSelect: (id: string) => void;
  maxDepth: number;
  searchQuery: string;
  activeFilter: 'all' | 'active' | 'inactive';
  selectedRank: string;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = dist.children.length > 0 && depth < maxDepth;
  const isSelected = selectedId === dist.id;
  const dotColor = getRankDotColor(dist.rank);
  const textColor = getRankColor(dist.rank);
  const isPexec = dist.rank === 'Provisional Executive';
  const isInactive = !dist.active;

  const matchesFilter = () => {
    const matchSearch = searchQuery === '' ||
      dist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dist.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchRank = selectedRank === 'All' || dist.rank === selectedRank;
    const matchActive = activeFilter === 'all' || (activeFilter === 'active' ? dist.active : !dist.active);
    return matchSearch && matchRank && matchActive;
  };

  if (!matchesFilter() && dist.children.length === 0) return null;

  return (
    <div className={`${depth > 0 ? 'ml-4 border-l border-zinc-800/60' : ''}`}>
      <div
        onClick={() => onSelect(dist.id)}
        className={`group flex items-center gap-2 px-2 py-1.5 mx-1 rounded-lg cursor-pointer transition-all duration-150 ${
          isSelected ? 'bg-violet-600/20 border border-violet-500/20' : 'hover:bg-zinc-800/50 border border-transparent'
        } ${isInactive ? 'opacity-60' : ''}`}
      >
        {/* Expand toggle */}
        <button
          onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
          className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${hasChildren ? 'text-zinc-500 hover:text-zinc-300' : 'opacity-0 pointer-events-none'}`}
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>

        {/* Rank dot */}
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor} ${isInactive ? 'opacity-50' : ''}`} />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className={`text-xs font-semibold truncate ${isSelected ? 'text-violet-200' : 'text-zinc-200'}`}>
              {dist.name.split(' ')[0]}
            </span>
            {isPexec && <AlertTriangle size={10} className="text-orange-400 flex-shrink-0" />}
            {dist.qualStage !== 'None' && dist.qualStage !== 'Qualified' && (
              <Zap size={10} className="text-amber-400 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`text-[9px] font-medium truncate ${textColor}`}>{dist.rank.replace('Brand Partner ', 'BP ')}</span>
            <span className="text-[9px] text-zinc-600 font-mono">{(dist.gsv/1000).toFixed(1)}K</span>
          </div>
        </div>

        {/* PSV */}
        <div className={`text-[9px] font-mono font-semibold flex-shrink-0 ${dist.psv >= 100 ? 'text-emerald-500' : 'text-red-500'}`}>
          {dist.psv}
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {dist.children.map(childId => {
            const child = distributors[childId];
            if (!child) return null;
            return (
              <TreeNode
                key={`tree-${childId}`}
                dist={child}
                distributors={distributors}
                depth={depth + 1}
                selectedId={selectedId}
                onSelect={onSelect}
                maxDepth={maxDepth}
                searchQuery={searchQuery}
                activeFilter={activeFilter}
                selectedRank={selectedRank}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function NetworkTree(props: NetworkTreeProps) {
  const root = props.distributors[props.rootId];
  if (!root) return null;

  return (
    <div className="py-2">
      {/* Legend */}
      <div className="px-3 pb-2 flex flex-wrap gap-1.5">
        {[
          { color: 'bg-zinc-500', label: 'Dist' },
          { color: 'bg-yellow-400', label: 'Gold' },
          { color: 'bg-purple-400', label: 'Exec' },
          { color: 'bg-orange-400', label: 'PExec' },
          { color: 'bg-emerald-400', label: 'Emr' },
          { color: 'bg-amber-400', label: 'Pres' },
        ].map(l => (
          <div key={`legend-${l.label}`} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${l.color}`} />
            <span className="text-[9px] text-zinc-600">{l.label}</span>
          </div>
        ))}
      </div>

      <TreeNode
        dist={root}
        distributors={props.distributors}
        depth={0}
        selectedId={props.selectedId}
        onSelect={props.onSelect}
        maxDepth={props.maxDepth}
        searchQuery={props.searchQuery}
        activeFilter={props.activeFilter}
        selectedRank={props.selectedRank}
      />
    </div>
  );
}